/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { getReferralEvents } from "../../api/referral";
import Card from "../shared/Card";
import Swal from "sweetalert2";
import { PuffLoader } from "react-spinners";
import { downloadExcel } from "../../api/excel";

const ReferralEventsTable = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    eventType: "Any",
    dateFrom: "",
    dateTo: "",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    fromRecord: 0,
    toRecord: 0,
    totalRecords: 0,
  });

  const [pageSize, setPageSize] = useState(10);

  const excel = async () => {
    try {
      const blob = await downloadExcel();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "eventos-referidos.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    }
  };

  const loadEvents = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getReferralEvents(page, pageSize);
      setEvents(response.data || []);

      if (response.meta) {
        setPagination({
          currentPage: response.meta.current_page || 1,
          lastPage: response.meta.last_page || 1,
          fromRecord: response.meta.from_record || 0,
          toRecord: response.meta.to_record || 0,
          totalRecords: response.meta.total_records || 0,
        });
      } else {
        setPagination({
          currentPage: 1,
          lastPage: 1,
          fromRecord: events.length > 0 ? 1 : 0,
          toRecord: events.length,
          totalRecords: events.length,
        });
      }
    } catch (error) {
      console.error("Error loading events:", error);
      setEvents([]);
      setPagination({
        currentPage: 1,
        lastPage: 1,
        fromRecord: 0,
        toRecord: 0,
        totalRecords: 0,
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los eventos de referidos",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(1);
  }, []);

  useEffect(() => {
    if (!loading) {
      loadEvents(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  // Función para cambiar de página
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.lastPage || loading) return;
    loadEvents(page);
  };

  // Función para cambiar el tamaño de página
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    if (newSize !== pageSize) {
      setPageSize(newSize);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEventTypeLabel = (type) => {
    const types = {
      click: "Click",
      registration: "Registro",
      sale: "Venta",
      subscribe: "Suscripción",
      "referrer change": "Cambio",
      "referrer abandonment": "Abandono",
      withdrawal: "Retiro",
    };
    return types[type] || type;
  };

  const filteredEvents = events.filter((event) => {
    if (filters.eventType !== "Any" && event.event_type !== filters.eventType) {
      return false;
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const idMatch = event.id.toLowerCase().includes(searchLower);
      if (!idMatch) {
        return false;
      }
    }

    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      const eventDate = new Date(event.created_at);
      if (eventDate < dateFrom) {
        return false;
      }
    }

    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setHours(23, 59, 59);
      const eventDate = new Date(event.created_at);
      if (eventDate > dateTo) {
        return false;
      }
    }

    return true;
  });

  const renderPagination = () => {
    const pages = [];
    const { currentPage, lastPage } = pagination;

    if (lastPage >= 1) {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(lastPage, startPage + 4);

      if (endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
      }

      if (startPage > 1) {
        pages.push(
          <button
            key="1"
            className={`pagination__button ${
              currentPage === 1 ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(1)}
            disabled={loading}
          >
            1
          </button>
        );
        if (startPage > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination__ellipsis">
              ...
            </span>
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`pagination__button ${
              currentPage === i ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(i)}
            disabled={loading}
          >
            {i}
          </button>
        );
      }

      if (endPage < lastPage) {
        if (endPage < lastPage - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination__ellipsis">
              ...
            </span>
          );
        }
        pages.push(
          <button
            key={lastPage}
            className={`pagination__button ${
              currentPage === lastPage ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(lastPage)}
            disabled={loading}
          >
            {lastPage}
          </button>
        );
      }
    }

    return (
      <div className="pagination">
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          &laquo;
        </button>
        {pages}
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === lastPage || loading}
        >
          &raquo;
        </button>
      </div>
    );
  };

  const renderPageSizeSelector = () => {
    return (
      <div className="pagination__size-selector">
        <label htmlFor="pageSize" className="pagination__size-label">
          Mostrar:
        </label>
        <select
          id="pageSize"
          className="filter-select pagination__size-select"
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={loading}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  return (
    <Card className={"referral-events-card"}>
      <div className="referral-events">
        <div className="referral-events__header">
          <h2 className="referral-events__title">Actividad de Referidos</h2>
          {renderPageSizeSelector()}
        </div>

        <div className="referral-events__excel-btn">
          <button
            onClick={() => {
              excel();
            }}
          >
            Descargar Excel
          </button>
        </div>

        <div className="referral-events__table">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Workspace ID</th>
                <th>Correo</th>
                <th>Tipo</th>
                <th>Monto (COP)</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    No hay eventos registrados
                  </td>
                </tr>
              ) : (
                filteredEvents.map((event) => (
                  <tr key={event.id}>
                    <td>
                      {event.workspace_owner
                        ? event.workspace_owner
                        : "Sin nombre"}
                    </td>
                    <td>{event.workspace_id ? event.workspace_id : "-"}</td>
                    <td>
                      {event.owner_email ? event.owner_email : "Sin correo"}
                    </td>
                    <td>
                      <span
                        className={`event-type event-type--${event.event_type.replace(
                          /\s+/g,
                          "-"
                        )}`}
                      >
                        {getEventTypeLabel(event.event_type)}
                      </span>
                    </td>
                    <td className="amount">
                      {event.amount ? `${event.amount}` : "-"}
                    </td>
                    <td>{formatDate(event.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Componente de paginación */}
        <div className="referral-events__pagination">
          <div className="pagination__container">
            {renderPagination()}
            <div className="pagination__info">
              Mostrando {pagination.fromRecord} a {pagination.toRecord} de{" "}
              {pagination.totalRecords} registros
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ReferralEventsTable;
